<template>
    <vx-modal :padding="false" v-model:visible="visible" size="md">
        <template v-slot:header>
            <div class="pt-6 px-6 pb-4">{{ isEditing ? 'Update' : 'New' }} Category</div>
        </template>

        <div class="p-6">
            <div class="mb-3">
                <vx-label>Color</vx-label>
                <div class="flex gap-2">
                    <div
                        v-for="option of THREAD_CATEGORY_COLORS_OPTIONS"
                        :key="option.value"
                        tabindex="0"
                        class="cursor-pointer hover:scale-110 active:scale-90 rounded size-10 transition outline outline-2 outline-offset-2 focus-visible:outline-blue-500"
                        :class="formData.color === option.value ? '' : 'outline-transparent'"
                        :style="{
                            backgroundColor: '#' + option.value,
                            ...(formData.color === option.value ? { outlineColor: '#' + option.value } : {}),
                        }"
                        @click="formData.color = option.value"
                    ></div>
                </div>
            </div>
            <div class="mb-3">
                <vx-label>Name</vx-label>
                <vx-input
                    data-test="name-input"
                    v-model="formData.name"
                    size="lg"
                    :error="displayFirstError(validation.name)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Description (optional)</vx-label>
                <vx-input
                    data-test="description-input"
                    v-model="formData.description"
                    size="lg"
                    :rows="3"
                    :error="displayFirstError(validation.description)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Classification Instructions</vx-label>
                <vx-input
                    data-test="instructions-input"
                    v-model="formData.classification_instructions"
                    size="lg"
                    :rows="4"
                    placeholder="Describe what makes a conversation belong to this category..."
                    :error="displayFirstError(validation.classification_instructions)"
                >
                    <template v-slot:hint> These instructions guide the AI in classifying conversations. </template>
                </vx-input>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" :disabled="props.syncing" @click="visible = false">
                    Cancel
                </vx-button>
                <vx-button
                    data-test="submit-btn"
                    class="grow"
                    color="primary"
                    size="lg"
                    type="button"
                    :loading="processing || props.syncing"
                    @click.prevent="submit()"
                >
                    {{ isEditing ? 'Update' : 'Create' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxInput, VxLabel, VxModal } from '@voxie/frontend-components';
import { required, maxLength } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { displayFirstError, groupAndOrgUnitName } from '~/utils/validation';
import { THREAD_CATEGORY_COLORS, THREAD_CATEGORY_COLORS_OPTIONS } from '~/constants/messageHub';
import type {
    ThreadCategoryFormData,
    ThreadCategoryType,
} from '~/services/threadCategories/threadCategoriesClient.types';

const props = defineProps<{
    category: ThreadCategoryType | null;
    editIndex: number | null;
    syncing?: boolean;
}>();

const emit = defineEmits<{
    saved: [data: ThreadCategoryFormData, editIndex: number | null];
}>();

const visible = defineModel<boolean>('visible', { required: true });

const isEditing = computed(() => null !== props.editIndex);

const createEmptyFormData = (): ThreadCategoryFormData => ({
    name: '',
    description: null,
    classification_instructions: '',
    color: THREAD_CATEGORY_COLORS.sky,
});

const formData = ref<ThreadCategoryFormData>(createEmptyFormData());

watch(visible, (isVisible) => {
    if (isVisible) {
        if (props.category) {
            formData.value = {
                name: props.category.name,
                description: props.category.description,
                classification_instructions: props.category.classification_instructions,
                color: props.category.color ?? THREAD_CATEGORY_COLORS.sky,
            };
        } else {
            formData.value = createEmptyFormData();
        }
    } else {
        validation.value.$reset();
    }
});

const { submit, processing, validation } = useFormSubmission({
    data: formData,
    validation: {
        name: { required, maxLength: maxLength(80), groupAndOrgUnitName },
        description: { maxLength: maxLength(280) },
        classification_instructions: { required, maxLength: maxLength(3000) },
    },
    submit: async () => {
        emit('saved', { ...formData.value }, props.editIndex);
    },
    preserveScroll: true,
});
</script>
